import React, { memo, useCallback, useRef, useState } from 'react'
import { SVGGroup } from '@pff-consumer/utils'
import ReCAPTCHA from 'react-google-recaptcha'
import { useSubscribeAnalyticsEvent } from '@pff-consumer/analytics/hooks/useSubscribeAnalyticsEvent'
import { PaypalPayment } from './paypal-payment/paypal-payment.web'
import { ApplePayPayment } from './apple-pay-payment/apple-pay-payment.web'
import { CreditCardPayment } from './credit-card-payment/credit-card-payment.web'
import { useStyles } from './one-step-checkout.styles'
import { CustomerEmail } from './customer-email/customer-email.web'
import { Button } from '../button/button.web'
import { useScrollToPosition } from '../../hooks/subscribe/useScrollToPosition'
import { CheckoutErrorModal } from './checkout-error-modal/checkout-error-modal.web'
import { CheckoutSummary } from '../checkout-summary/checkout-summary.web'
import { useApplePayAccess } from '../../hooks/subscribe/useApplePayAccess'

import { OneStepCheckoutProps } from './one-step-checkout.props'

export const OneStepCheckout = memo((props: OneStepCheckoutProps) => {
  const {
    plan,
    price,
    isLoggedIn,
    onComplete,
    onError,
    onDisabledClick,
    onRecurlyTokenInit,
    isEmailValid,
    isOnCompletePending,
    isPayPalProcessing,
    isCreditCardProcessing,
    isApplePayProcessing,
    isDiscountApplied,
    showKitSummaryRow,
  } = props
  const [isFormOpen, setFormOpen] = useState<boolean>(false)
  const { scrollToPosition } = useScrollToPosition()
  const { sendSubscribeAnalyticsEvent } = useSubscribeAnalyticsEvent()
  const { canShowApplePay } = useApplePayAccess()
  const styles = useStyles({ canShowApplePay })
  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const creditCardIconProps = {
    iconName: 'credit-card',
    iconGroup: SVGGroup.Icons,
    iconHeight: 18,
    iconWidth: 24,
  }
  const continueWithCard = () => {
    setFormOpen((formOpen) => !formOpen)

    if (isFormOpen) {
      scrollToPosition(0)
    } else {
      scrollToPosition(document.documentElement.scrollHeight) // scrolls to the bottom
    }

    sendSubscribeAnalyticsEvent('CTA Clicked', {
      ctaName: 'Credit Card',
      ctaLocation: 'Single Page Checkout',
    })
  }

  const onCallbackWithRecaptcha = useCallback(
    async (recurlyToken: string, paymentType: string) => {
      if (recaptchaRef.current) {
        const token = await recaptchaRef.current.executeAsync()
        if (token) {
          await onComplete(recurlyToken, paymentType, token)
        }
      }
      return null
    },
    [onComplete]
  )

  return (
    <section className={styles.oneStepCheckoutContainer}>
      {!isLoggedIn ? (
        <>
          <header className={styles.oneStepCheckoutHeader}>Create Account and Checkout</header>
          <CustomerEmail />
        </>
      ) : (
        <header className={styles.oneStepCheckoutHeader}>Checkout</header>
      )}
      <CheckoutSummary
        selectedPlan={plan}
        price={price}
        isDiscountApplied={isDiscountApplied}
        showKitRow={showKitSummaryRow}
      />
      <div className={styles.paymentMethodsContainer}>
        {canShowApplePay && (
          <ApplePayPayment
            isDisabled={!isEmailValid}
            isOnCompletePending={isOnCompletePending}
            onComplete={onCallbackWithRecaptcha}
            onDisabledClick={onDisabledClick}
            onError={onError}
            price={price}
            plan={plan}
            onRecurlyTokenInit={onRecurlyTokenInit}
            isPaymentProcessing={isApplePayProcessing}
            size='sm'
          />
        )}

        <PaypalPayment
          isDisabled={!isEmailValid}
          onComplete={onCallbackWithRecaptcha}
          onDisabledClick={onDisabledClick}
          onError={onError}
          onRecurlyTokenInit={onRecurlyTokenInit}
          isPaymentProcessing={isPayPalProcessing}
          size='sm'
        />
        {/* Credit card button */}
        <Button
          text='Credit Card'
          onClick={!isEmailValid ? onDisabledClick : continueWithCard}
          data-analytics-id='credit-card-button'
          disabled={isOnCompletePending}
          size='sm'
          {...creditCardIconProps}
        />
      </div>
      {/* Credit card form */}
      {isFormOpen && (
        <CreditCardPayment
          isDisabled={!isEmailValid}
          isOnCompletePending={isOnCompletePending}
          onComplete={onCallbackWithRecaptcha}
          onError={onError}
          price={price}
          plan={plan}
          onRecurlyTokenInit={onRecurlyTokenInit}
          isPaymentProcessing={isCreditCardProcessing}
          isFormOpen={isFormOpen}
        />
      )}

      <CheckoutErrorModal />

      <ReCAPTCHA
        ref={recaptchaRef}
        size='invisible'
        sitekey={process.env.RECAPTCHA_SITE_KEY!}
      />
    </section>
  )
})
